import { Injectable, inject } from '@angular/core';
import { DateTime } from 'luxon';
import { Observable, combineLatest, firstValueFrom, from, map, of, switchMap } from 'rxjs';
import { dateRangeToDateStringArray } from '@curbnturf/helpers';
import { ListingApiService } from '../backend-api/listing-api.service';
import { ReservationApiService } from '../backend-api/reservation-api.service';
import { HostAvailabilityService } from '../host/host-availability.service';
import { isoKey } from '../shared/iso-date.util';

/** Every ISO date from today through the listing's own booking horizon
 *  ([reserveDaysInAdvance, bookingWindow], mirroring the same bound
 *  `PriceCalculationService.calendarDates` uses server-side) — the set of
 *  days it's even meaningful to ask "is this available?" about. A day
 *  outside this window isn't bookable yet regardless of blackout state. */
function potentialDaysFor(reserveDaysInAdvance: number | undefined, bookingWindow: number | undefined): string[] {
  const start = DateTime.now().plus({ days: reserveDaysInAdvance ?? 0 });
  const end = DateTime.now().plus({ days: bookingWindow ?? 365 });
  return dateRangeToDateStringArray(start, end);
}

/** Real availability for a listing, sourced from every one of its sites'
 *  actual bookable-days calendar (`GET /reservation/calendar/:siteId` —
 *  blackout dates and other guests' confirmed reservations, already
 *  computed server-side via `Site.getBusyDays()`). A day counts as
 *  available for the listing as a whole if at least one of its sites is
 *  bookable that day — the guest picks a specific site later. */
@Injectable({ providedIn: 'root' })
export class ListingAvailabilityService {
  private hostAvailability = inject(HostAvailabilityService);
  private listingSvc = inject(ListingApiService);
  private reservationSvc = inject(ReservationApiService);

  /** Real per-day post-fee prices for a listing, keyed by ISO date — the minimum
   *  across its sites for dates where more than one site reports a price. Populated
   *  as a side effect of unavailableSet$'s calendar fetch (GET reservation/calendar/:siteId,
   *  already post-fee server-side via PriceCalculationService.calendarDates). Empty until
   *  that fetch resolves once for a given listingId. */
  private calendarPriceCache = new Map<number, Record<string, number>>();

  /** Same real per-day post-fee prices, but per site rather than aggregated across a
   *  listing — drives the multi-site picker's per-site average. Populated in the same
   *  pass as calendarPriceCache, keyed by siteId. */
  private sitePriceCache = new Map<number, Record<string, number>>();

  /** Real per-day combined tax amount for a listing, keyed by ISO date — the same
   *  min-across-sites day picked by calendarPriceCache carries its own tax alongside.
   *  Populated in the same pass; see effectiveTaxForRange. */
  private calendarTaxCache = new Map<number, Record<string, number>>();

  /** [earliest, latest] ISO date a listing can actually be booked for — derived from
   *  its reserveDaysInAdvance/bookingWindow, the same bounds potentialDaysFor() uses to
   *  build unavailableSet$'s Set. Populated in the same pass. A date outside this range
   *  is never in unavailableSet either way (it's outside the range unavailableSet$ even
   *  considers), so callers gating date-picker selection need both this AND
   *  unavailableSet — see bookingBounds(). */
  private bookingBoundsCache = new Map<number, { minIso: string; maxIso: string }>();

  /** Unavailable-date set for one listing. Emits once per subscription
   *  with the current real state (the underlying HTTP calls aren't a
   *  live stream) — callers that need to react to it changing should
   *  re-subscribe when their own trigger (e.g. a re-focused page) fires.
   *
   *  As a side effect, also populates calendarPriceCache/sitePriceCache from the same
   *  calendar fetch — see effectiveNightlyPrice/averageNightlyPriceForSite. */
  unavailableSet$(listingId: number): Observable<Set<string>> {
    return from(firstValueFrom(this.listingSvc.fetch(listingId))).pipe(
      switchMap(listing => {
        const potentialDays = potentialDaysFor(listing.reserveDaysInAdvance, listing.bookingWindow);
        if (potentialDays.length > 0) {
          this.bookingBoundsCache.set(listingId, {
            minIso: potentialDays[0],
            maxIso: potentialDays[potentialDays.length - 1],
          });
        }
        const sites = (listing.sites ?? []).filter(s => s.available !== false && s.id != null);
        if (sites.length === 0) {
          this.calendarPriceCache.set(listingId, {});
          this.calendarTaxCache.set(listingId, {});
          return of(new Set(potentialDays));
        }

        return combineLatest(sites.map(s => this.reservationSvc.calendarDates(s.id as number))).pipe(
          map(calendars => {
            const availableUnion = new Set<string>();
            const listingPrices: Record<string, number> = {};
            const listingTaxes: Record<string, number> = {};
            sites.forEach((site, i) => {
              const siteId = site.id as number;
              const sitePrices: Record<string, number> = {};
              for (const day of calendars[i]) {
                availableUnion.add(day.date);
                if (typeof day.price === 'number') {
                  sitePrices[day.date] = day.price;
                  const existing = listingPrices[day.date];
                  if (existing === undefined || day.price < existing) {
                    listingPrices[day.date] = day.price;
                    listingTaxes[day.date] = day.tax ?? 0;
                  }
                }
              }
              this.sitePriceCache.set(siteId, sitePrices);
            });
            this.calendarPriceCache.set(listingId, listingPrices);
            this.calendarTaxCache.set(listingId, listingTaxes);
            return new Set(potentialDays.filter(iso => !availableUnion.has(iso)));
          }),
        );
      }),
    );
  }

  /** Unavailable days for moving an existing reservation: only the booked
   *  site, with the reservation's own nights counted as free (otherwise the
   *  guest can't keep or overlap their current dates). */
  unavailableSetForReservation$(listingId: number, siteId: number, reservationId: number): Observable<Set<string>> {
    return from(firstValueFrom(this.listingSvc.fetch(listingId))).pipe(
      switchMap(listing => {
        const potentialDays = potentialDaysFor(listing.reserveDaysInAdvance, listing.bookingWindow);
        if (potentialDays.length > 0) {
          this.bookingBoundsCache.set(listingId, {
            minIso: potentialDays[0],
            maxIso: potentialDays[potentialDays.length - 1],
          });
        }
        return this.reservationSvc.calendarDatesExcluding(siteId, reservationId).pipe(
          map(calendar => {
            const available = new Set(calendar.map(day => day.date));
            return new Set(potentialDays.filter(iso => !available.has(iso)));
          }),
        );
      }),
    );
  }

  /** Primes calendarPriceCache/sitePriceCache for a listing without needing the
   *  unavailable-set result — for callers (e.g. booking-review, reachable via deep
   *  link without ever visiting /listing/:id) that only need effectivePricesForRange/
   *  subtotal correct. Reuses unavailableSet$'s fetch; the returned Set is unused. */
  primeCalendarPrices$(listingId: number): Observable<void> {
    return this.unavailableSet$(listingId).pipe(map(() => undefined));
  }

  /** Async range check — real HTTP round trip under the hood (per-site
   *  calendar), so callers that need this synchronously (e.g. a list
   *  filter) should cache the result rather than call this inline. */
  isAvailableForRange$(listingId: number, startIso: string, endIso: string): Observable<boolean> {
    const start = new Date(startIso + 'T00:00:00');
    const end = new Date(endIso + 'T00:00:00');
    if (!(start < end)) return of(true);
    return this.unavailableSet$(listingId).pipe(
      map(unavailable => {
        for (let d = new Date(start); d < end; d = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1)) {
          if (unavailable.has(isoKey(d))) return false;
        }
        return true;
      }),
    );
  }

  /** Real nightly price for one date — real calendar price (server-computed,
   *  post-fee) > per-day override > seasonal tier — or `undefined` if none of
   *  those have a real signal for this date (e.g. a blackout day, absent from
   *  the calendar entirely, or before the calendar fetch resolves). Kept
   *  distinct from `effectiveNightlyPrice`'s `basePrice`-filled variant so
   *  callers that need to tell "no real data for this date" apart from "the
   *  real price happens to equal basePrice" can (see `lowestNightlyForRange`,
   *  which must NOT let a gap day's fallback outcompete real post-fee prices
   *  in a min() — basePrice is pre-fee and always looks "cheaper"). */
  private realNightlyPrice(listingId: number, iso: string): number | undefined {
    const calendarPrice = this.calendarPriceCache.get(listingId)?.[iso];
    if (typeof calendarPrice === 'number') return calendarPrice;
    const avail = this.hostAvailability.get(listingId);
    const override = avail.prices?.[iso];
    if (typeof override === 'number') return override;
    const tier = avail.pricingTiers?.find(t => iso >= t.start && iso <= t.end);
    return tier?.nightlyPrice;
  }

  /** Effective nightly price for one date — `realNightlyPrice()`, filled with
   *  `basePrice` when there's no real signal for this date. Fine for a
   *  guest-selected range (already validated available night-by-night via
   *  `dateFilter`/`rangeHasUnavailable`, so a real gap should never actually
   *  occur here) but NOT safe for scanning an unvalidated window for a
   *  floor/minimum — see `lowestNightlyForRange`. */
  effectiveNightlyPrice(listingId: number, iso: string, basePrice: number): number {
    return this.realNightlyPrice(listingId, iso) ?? basePrice;
  }

  /** Average real post-fee nightly price for one site across [startIso, endIso),
   *  sourced from sitePriceCache (populated by unavailableSet$'s calendar fetch).
   *  Returns undefined when nothing's cached yet for this site/range — callers
   *  should fall back to a raw price in that case, same transient-fallback
   *  convention as effectiveNightlyPrice's basePrice branch. */
  averageNightlyPriceForSite(siteId: number, startIso: string, endIso: string): number | undefined {
    const record = this.sitePriceCache.get(siteId);
    if (!record) return undefined;
    const values = Object.entries(record)
      .filter(([iso]) => iso >= startIso && iso < endIso)
      .map(([, price]) => price);
    if (values.length === 0) return undefined;
    const sum = values.reduce((a, b) => a + b, 0);
    return Math.round(sum / values.length);
  }

  /** Lowest REAL nightly price across [startIso, endIso) — drives the
   *  "from $X" headline on /listing/:id. Considers only dates with an actual
   *  price signal (`realNightlyPrice`); a date with none (e.g. a blackout day
   *  absent from the calendar) is skipped rather than filled with `basePrice`
   *  — since `basePrice` is the raw pre-fee rate, filling gaps with it would
   *  make an ordinary blocked day anywhere in the window always "win" the
   *  minimum over every real, post-fee calendar price. Falls back to
   *  `basePrice` only when the whole range has no real signal at all (e.g.
   *  before the calendar fetch resolves). */
  lowestNightlyForRange(listingId: number, startIso: string, endIso: string, basePrice: number): number {
    const start = new Date(startIso + 'T00:00:00');
    const end = new Date(endIso + 'T00:00:00');
    if (!(start < end)) return basePrice;
    let min: number | null = null;
    for (let d = new Date(start); d < end; d = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1)) {
      const price = this.realNightlyPrice(listingId, isoKey(d));
      if (price !== undefined && (min === null || price < min)) min = price;
    }
    return min ?? basePrice;
  }

  /** Per-night effective prices across [startIso, endIso) — booking
   *  convention, last night IS the checkout-eve, checkout day is free. */
  effectivePricesForRange(
    listingId: number,
    startIso: string,
    endIso: string,
    basePrice: number,
  ): Record<string, number> {
    const out: Record<string, number> = {};
    const start = new Date(startIso + 'T00:00:00');
    const end   = new Date(endIso   + 'T00:00:00');
    if (!(start < end)) return out;
    for (let d = new Date(start); d < end; d = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1)) {
      const iso = isoKey(d);
      out[iso] = this.effectiveNightlyPrice(listingId, iso, basePrice);
    }
    return out;
  }

  /** Sum of real combined per-day tax across [startIso, endIso) — booking convention,
   *  last night IS the checkout-eve, checkout day is free. Only dates with a real
   *  calendar signal contribute (see calendarTaxCache, populated alongside
   *  calendarPriceCache); returns 0 when nothing's cached yet or no tax applies. */
  effectiveTaxForRange(listingId: number, startIso: string, endIso: string): number {
    const record = this.calendarTaxCache.get(listingId);
    if (!record) return 0;
    const start = new Date(startIso + 'T00:00:00');
    const end = new Date(endIso + 'T00:00:00');
    if (!(start < end)) return 0;
    let total = 0;
    for (let d = new Date(start); d < end; d = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1)) {
      total += record[isoKey(d)] ?? 0;
    }
    return total;
  }

  /** Earliest/latest ISO date a listing can be booked for (from reserveDaysInAdvance/
   *  bookingWindow) — pairs with unavailableSet$'s Set to fully gate a date-picker
   *  (`[minDate]`/`[maxDate]` for this range, `[dateFilter]` against unavailableSet for
   *  blackout/reserved days within it). Undefined until unavailableSet$'s fetch resolves
   *  once for a given listingId. */
  bookingBounds(listingId: number): { minIso: string; maxIso: string } | undefined {
    return this.bookingBoundsCache.get(listingId);
  }

  /** Min/max-stay rule check for a chosen [start, end). The rule that
   *  applies is the one whose [start, end] contains the check-in date
   *  (booking convention — guest's check-in night is the gate).
   *
   *  No real backend for stay rules yet (see host-listing-calendar's
   *  commented-out min-stay UI) — always passes today until that's real. */
  checkStayRule(
    listingId: number,
    startIso: string,
    endIso: string,
  ): { ok: true } | { ok: false; kind: 'min' | 'max'; requiredNights: number } {
    const rules = this.hostAvailability.get(listingId).stayRules;
    if (!rules || rules.length === 0) return { ok: true };
    const start = new Date(startIso + 'T00:00:00');
    const end   = new Date(endIso + 'T00:00:00');
    const nights = Math.round((end.getTime() - start.getTime()) / 86_400_000);
    if (nights <= 0) return { ok: true };
    const rule = rules.find(r => startIso >= r.start && startIso <= r.end);
    if (!rule) return { ok: true };
    if (rule.minNights != null && nights < rule.minNights) {
      return { ok: false, kind: 'min', requiredNights: rule.minNights };
    }
    if (rule.maxNights != null && nights > rule.maxNights) {
      return { ok: false, kind: 'max', requiredNights: rule.maxNights };
    }
    return { ok: true };
  }
}
